<template>
  <div class="page-wrapper">
    <div class="container-fluid px-0 py-0">
      <div class="main-card shadow-sm">
        <div
          class="card-header-custom border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <h1 class="h4 mb-0 fw-bold text-dark">{{ computedTitle.label }}</h1>
            <span class="badge bg-light text-dark border shadow-sm">
              <i class="fas fa-tags text-primary me-1"></i>
              Listado
            </span>
          </div>

          <div>
            <button
              @click="openModalImportar"
              class="btn btn-primary btn-sm shadow-sm d-flex align-items-center hover-elevate"
            >
              <i class="fas fa-file-import me-2"></i> Importar
            </button>
          </div>
        </div>

        <div class="p-4 bg-light border-bottom">
          <div class="row">
            <div class="col-md-5">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0 text-muted">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  v-model="searchTerm"
                  class="form-control border-start-0 ps-0"
                  placeholder="Buscar por marca, modelo o versión..."
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div v-if="!loading && prices.length === 0" class="text-center py-5">
            <div class="text-secondary mb-3">
              <i class="fas fa-car fa-3x opacity-50"></i>
            </div>
            <p class="text-muted">No se encontraron precios.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead class="bg-light text-secondary text-uppercase small">
                <tr>
                  <th class="ps-4 py-1 border-0">Marca</th>
                  <th class="py-1 border-0">Modelo</th>
                  <th class="py-1 border-0">Versión</th>
                  <th class="py-1 border-0 text-center">Moneda</th>
                  <th class="py-1 border-0 text-end">0km</th>
                  <th class="pe-4 py-1 border-0 text-end">Opciones</th>
                </tr>
              </thead>
              <tbody>
                <TableSpinner
                  v-if="loading"
                  :colspan="6"
                  text="Cargando precios..."
                />

                <template v-else>
                  <tr v-for="price in prices" :key="price.id">
                    <td class="ps-4 py-1 fw-bold text-dark">
                      {{ price.brand }}
                    </td>

                    <td class="py-1 text-dark">
                      {{ price.model }}
                    </td>

                    <td class="py-1 text-muted small">
                      {{ price.version }}
                    </td>

                    <td class="py-1 text-center">
                      <span class="badge bg-light text-dark border">
                        {{ price.currency }}
                      </span>
                    </td>

                    <td
                      class="py-1 text-end fw-bold text-success text-monospace"
                    >
                      {{ price["0km"] }}
                    </td>

                    <td class="pe-4 py-1 text-end">
                      <ActionDropdown>
                        <button
                          class="dropdown-item d-flex align-items-center"
                          @click="viewMore(price)"
                        >
                          <i class="fas fa-eye text-info fa-fw me-2"></i> Ver
                          detalle
                        </button>
                      </ActionDropdown>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div
          class="card-footer-custom p-3 bg-light border-top d-flex justify-content-end"
          v-if="lastPage > 1"
        >
          <div class="btn-group shadow-sm">
            <button
              @click="fetchPrices(1)"
              class="btn btn-white border"
              :disabled="currentPage === 1 || loading"
            >
              Primera
            </button>
            <button
              @click="fetchPrices(currentPage - 1)"
              class="btn btn-white border"
              :disabled="currentPage === 1 || loading"
            >
              Anterior
            </button>
            <button
              @click="fetchPrices(currentPage + 1)"
              class="btn btn-white border"
              :disabled="!hasMorePages || loading"
            >
              Siguiente
            </button>
            <button
              @click="fetchPrices(lastPage)"
              class="btn btn-white border"
              :disabled="currentPage === lastPage || loading"
            >
              Última
            </button>
          </div>
        </div>
      </div>
    </div>

    <ModalFormPrecios
      @import-completed="fetchPrices"
      @export-completed="handleExportCompleted"
      ref="modalFormPrecios"
      :subMenu="computedTitle"
      @closeModal="closeModal"
    />

    <ModalDetails
      v-if="modalData"
      :modalData="modalData"
      @close="modalData = null"
    />
  </div>
</template>

<script>
import ModalFormPrecios from "@/components/precios/ModalFormPrecios.vue";
import ModalDetails from "@/components/precios/ModalDetails.vue";
import { sidebarMenuItemsService } from "@/services/api/sidebarMenuItemsService.js";
import { fetchPricesApi, searchPrices } from "@/services/api/priceService.js";
import { debounce } from "lodash";

// CAMBIO: Se usa TableSpinner en lugar de LoadingSpinner
import TableSpinner from "@/components/ui/TableSpinner.vue";
import ActionDropdown from "@/components/common/ActionDropdown.vue";

import "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
  name: "PreciosView",
  components: {
    ModalFormPrecios,
    ModalDetails,
    TableSpinner, // Registrado
    ActionDropdown,
  },

  data() {
    return {
      modalData: null,
      prices: [],
      loading: false,
      error: null,
      lastPage: 1,
      currentPage: 1,
      perPage: 15,
      hasMorePages: false,
      searchTerm: "",
      selectedType: null,
      menuItemsPrecio:
        sidebarMenuItemsService.find((item) => item.link === "/main/precios") ||
        {},
    };
  },

  created() {
    this.updateType();
    this.debouncedSearch = debounce(this.performSearch, 500);
  },

  computed: {
    computedTitle() {
      const selectedMenuItem = this.menuItemsPrecio.subMenu.find(
        (item) => item.link === `/main/precios/${this.$route.params.subMenu}`,
      );
      return selectedMenuItem || { label: "No encontrado" };
    },
  },

  methods: {
    openModalImportar() {
      this.$refs.modalFormPrecios.showModal();
    },
    closeModal() {
      this.$refs.modalFormPrecios.hideModal();
    },

    async handleExportCompleted() {
      await this.fetchPrices();
    },

    viewMore(price) {
      this.modalData = price;
    },

    async fetchPrices(page = 1) {
      this.loading = true;
      try {
        const response = await fetchPricesApi({
          page: page,
          perPage: this.perPage,
          search: this.searchTerm,
          type: this.selectedType ? this.selectedType.value : null,
        });
        if (response.data) {
          this.prices = response.data.data;
          this.currentPage = response.data.meta.current_page;
          this.hasMorePages = response.data.links.next !== null;
          this.lastPage = response.data.meta.last_page;
        } else {
          this.error = response.data.message;
        }
      } catch (error) {
        this.error = error.response
          ? error.response.data.message
          : error.message;
      } finally {
        this.loading = false;
      }
    },

    updateType() {
      this.selectedType = this.menuItemsPrecio.subMenu.find(
        (item) => item.link === `/main/precios/${this.$route.params.subMenu}`,
      );
      this.fetchPrices();
    },

    async performSearch() {
      if (!this.selectedType || !this.selectedType.value) {
        this.error = "El tipo de vehículo es obligatorio.";
        return;
      }

      this.loading = true;

      try {
        const response = await searchPrices({
          page: 1,
          perPage: this.perPage,
          search: this.searchTerm,
          type: this.selectedType.value,
        });
        if (response.data) {
          this.prices = response.data.data;
          this.currentPage = response.data.meta.current_page;
          this.hasMorePages = response.data.links.next !== null;
          this.lastPage = response.data.meta.last_page;
        } else {
          this.error = response.data.message;
        }
      } catch (error) {
        this.error = error.response
          ? error.response.data.message
          : error.message;
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    searchTerm() {
      this.debouncedSearch();
    },
    "$route.params.subMenu"() {
      this.updateType();
    },
  },
};
</script>

<style scoped>
/* Los estilos se mantienen igual */
.page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}
.main-card {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.card-header-custom {
  padding: 1.5rem 2rem;
  background-color: white;
}
.card-footer-custom {
  background-color: #f8f9fa;
}

.input-group-text {
  background-color: white;
  border-color: #ced4da;
}
.form-control {
  border-color: #ced4da;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
}
.form-control:focus {
  box-shadow: none;
  border-color: #86b7fe;
  background-color: #fbfdff;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}
.text-monospace {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}
.fa-fw {
  width: 1.25em;
  text-align: center;
}

.btn-white {
  background-color: white;
  color: #495057;
}
.btn-white:hover {
  background-color: #e9ecef;
}

.table-responsive::-webkit-scrollbar {
  height: 8px;
}
.table-responsive::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

.hover-elevate:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s;
}

@media (max-width: 768px) {
  .card-header-custom {
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
